import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Settings2, ChevronRight, ChevronDown, Edit, Download, History, Clock, Plus, CalendarPlus } from 'lucide-react';
import { useSchedulerConnection } from '@/hooks/use-scheduler-connection';
import { 
  formatHours, 
  formatPercent,
  getResourceBudgetStatus,
  getResourceBudgetStatusLabel,
  getResourceBudgetStatusColor
} from '@/lib/budget-utils';
import type { ResourceBudget } from '@/types/budget.types';
import type { ResourceBudgetSettings } from '@/types/resource-budget-settings.types';

interface MonthlyResourceBudgetViewProps {
  projectId: number;
  data: ResourceBudget;
  settings: ResourceBudgetSettings;
  onEdit?: () => void;
  onExport?: () => void;
  onHistory?: () => void;
  onRaiseRequest?: (roleName?: string) => void;
}

interface MonthlyData {
  month: string;
  budgetHours: number;
  actualHours: number;
  remainingHours: number;
  percentUsed: number;
}

interface RoleMonthlyData {
  role: string;
  totalBudgetHours: number;
  totalActualHours: number;
  months: MonthlyData[];
}

/**
 * Monthly Resource Budget View Component
 * Displays resource budgets broken down by month with expandable role details
 */
export function MonthlyResourceBudgetView({ projectId, data, settings, onEdit, onExport, onHistory, onRaiseRequest }: MonthlyResourceBudgetViewProps) {
  const [expandedRoles, setExpandedRoles] = useState<Set<string>>(new Set());
  const { isConnected: schedulerConnected } = useSchedulerConnection();

  if (!data || !data.summary || !data.byRole) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <Card className="w-full max-w-md border-dashed">
          <CardContent className="flex flex-col items-center justify-center text-center p-12">
            <div className="rounded-full bg-muted p-4 mb-4">
              <Clock className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No Resource Budget</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm">
              Plan team capacity and track hours by creating a resource budget for this project.
            </p>
            {onEdit && (
              <Button onClick={onEdit}>
                <Plus className="mr-2 h-4 w-4" />
                Create Resource Budget
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const { summary, byRole } = data;

  // Generate mock monthly data based on the existing data
  const monthlyRoleData = generateMonthlyMockData(byRole, settings);

  // Calculate monthly totals across all roles
  const monthlyTotals = calculateMonthlyTotals(monthlyRoleData);

  const toggleRole = (role: string) => {
    const newExpanded = new Set(expandedRoles);
    if (newExpanded.has(role)) {
      newExpanded.delete(role);
    } else {
      newExpanded.add(role);
    }
    setExpandedRoles(newExpanded);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle>Resource Budget (Monthly)</CardTitle>
          <div className="flex items-center gap-2">
            {onHistory && (
              <Button variant="outline" size="sm" onClick={onHistory}>
                <History className="mr-2 h-4 w-4" />
                History
              </Button>
            )}
            {onExport && (
              <Button variant="outline" size="sm" onClick={onExport}>
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            )}
            {onEdit && (
              <Button size="sm" onClick={onEdit}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Button>
            )}
            {/* TODO: Remove mock flag when scheduler connection comes from API */}
            {schedulerConnected && onRaiseRequest && (
              <Button
                size="sm"
                variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                onClick={() => onRaiseRequest()}
              >
                <CalendarPlus className="mr-2 h-4 w-4" />
                Raise Resource Request
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
            >
              <Settings2 className="h-4 w-4" />
              Configure View
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {monthlyRoleData.map((roleData) => (
              <div key={roleData.role} className="border rounded-lg">
                {/* Role Summary Row */}
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => toggleRole(roleData.role)}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                    >
                      {expandedRoles.has(roleData.role) ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </Button>
                    <div className="font-medium">{roleData.role}</div>
                  </div>
                  <div className="flex items-center gap-6 text-sm">
                    <div className="text-right">
                      <div className="font-medium">{formatHours(roleData.totalBudgetHours)}</div>
                      <div className="text-xs text-muted-foreground">Budget (h)</div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{formatHours(roleData.totalActualHours)}</div>
                      <div className="text-xs text-muted-foreground">Actual (h)</div>
                    </div>
                    <div className="w-24">
                      <Badge className={getResourceBudgetStatusColor(
                        getResourceBudgetStatus(roleData.totalActualHours, roleData.totalBudgetHours)
                      ).badge}>
                        {getResourceBudgetStatusLabel(
                          getResourceBudgetStatus(roleData.totalActualHours, roleData.totalBudgetHours)
                        )}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Monthly Details Table */}
                {expandedRoles.has(roleData.role) && (
                  <div className="border-t">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="font-bold">Month</TableHead>
                          <TableHead className="font-bold text-right">Budget (h)</TableHead>
                          <TableHead className="font-bold text-right">Actual (h)</TableHead>
                          <TableHead className="font-bold text-right">Remaining (h)</TableHead>
                          <TableHead className="font-bold text-right">% Used</TableHead>
                          <TableHead className="font-bold text-center">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {roleData.months.map((month) => {
                          const status = getResourceBudgetStatus(month.actualHours, month.budgetHours);
                          return (
                            <TableRow key={month.month}>
                              <TableCell className="font-medium">{formatMonthDisplay(month.month)}</TableCell>
                              <TableCell className="text-right">{formatHours(month.budgetHours)}</TableCell>
                              <TableCell className="text-right">{formatHours(month.actualHours)}</TableCell>
                              <TableCell className="text-right">
                                {month.remainingHours < 0 ? (
                                  <span className="text-destructive font-semibold">{formatHours(month.remainingHours)}</span>
                                ) : (
                                  formatHours(month.remainingHours)
                                )}
                              </TableCell>
                              <TableCell className="text-right">{formatPercent(month.percentUsed)}</TableCell>
                              <TableCell className="text-center">
                                <Badge className={getResourceBudgetStatusColor(status).badge}>
                                  {getResourceBudgetStatusLabel(status)}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Monthly Totals Summary */}
          <div className="mt-6 border-t pt-4">
            <h4 className="font-semibold mb-4">Monthly Totals Across All Roles</h4>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-bold">Month</TableHead>
                    <TableHead className="font-bold text-right">Budget (h)</TableHead>
                    <TableHead className="font-bold text-right">Actual (h)</TableHead>
                    <TableHead className="font-bold text-right">Remaining (h)</TableHead>
                    <TableHead className="font-bold text-right">% Used</TableHead>
                    <TableHead className="font-bold text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {monthlyTotals.map((month) => {
                    const status = getResourceBudgetStatus(month.actualHours, month.budgetHours);
                    return (
                      <TableRow key={month.month}>
                        <TableCell className="font-medium">{formatMonthDisplay(month.month)}</TableCell>
                        <TableCell className="text-right">{formatHours(month.budgetHours)}</TableCell>
                        <TableCell className="text-right">{formatHours(month.actualHours)}</TableCell>
                        <TableCell className="text-right">
                          {month.remainingHours < 0 ? (
                            <span className="text-destructive font-semibold">{formatHours(month.remainingHours)}</span>
                          ) : (
                            formatHours(month.remainingHours)
                          )}
                        </TableCell>
                        <TableCell className="text-right">{formatPercent(month.percentUsed)}</TableCell>
                        <TableCell className="text-center">
                          <Badge className={getResourceBudgetStatusColor(status).badge}>
                            {getResourceBudgetStatusLabel(status)}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Generate mock monthly data by distributing the role's total hours across months
 */
function generateMonthlyMockData(
  roles: ResourceBudget['byRole'],
  settings: ResourceBudgetSettings
): RoleMonthlyData[] {
  // Generate 6 months of data (current month + 5 future months)
  const months = generateMonthsList(6);

  return roles.map((role) => {
    // Distribute hours across months (with some variation)
    const monthlyData: MonthlyData[] = months.map((month, index) => {
      // Create variation in distribution (front-loaded with some randomness)
      const baseAllocation = role.budgetHours / months.length;
      const variation = index === 0 ? 1.3 : index === 1 ? 1.1 : 0.9; // Front-loaded
      const budgetHours = Math.round(baseAllocation * variation);
      
      // Actual hours are slightly less than budget (with some variation)
      const actualHours = Math.round(budgetHours * (0.7 + Math.random() * 0.3));
      
      const remainingHours = budgetHours - actualHours;
      const percentUsed = budgetHours > 0 ? (actualHours / budgetHours) * 100 : 0;

      return {
        month,
        budgetHours,
        actualHours,
        remainingHours,
        percentUsed,
      };
    });

    return {
      role: role.role,
      totalBudgetHours: role.budgetHours,
      totalActualHours: role.actualHours,
      months: monthlyData,
    };
  });
}

/**
 * Calculate monthly totals across all roles
 */
function calculateMonthlyTotals(roleData: RoleMonthlyData[]): MonthlyData[] {
  if (roleData.length === 0) return [];

  const monthCount = roleData[0].months.length;
  const totals: MonthlyData[] = [];

  for (let i = 0; i < monthCount; i++) {
    const monthData = roleData.reduce(
      (acc, role) => {
        const month = role.months[i];
        return {
          month: month.month,
          budgetHours: acc.budgetHours + month.budgetHours,
          actualHours: acc.actualHours + month.actualHours,
          remainingHours: acc.remainingHours + month.remainingHours,
          percentUsed: 0, // Will calculate after
        };
      },
      {
        month: '',
        budgetHours: 0,
        actualHours: 0,
        remainingHours: 0,
        percentUsed: 0,
      }
    );

    // Calculate percentage used
    monthData.percentUsed = monthData.budgetHours > 0 
      ? (monthData.actualHours / monthData.budgetHours) * 100 
      : 0;

    totals.push(monthData);
  }

  return totals;
}

/**
 * Generate a list of month strings (YYYY-MM format)
 */
function generateMonthsList(count: number): string[] {
  const months: string[] = [];
  const now = new Date();
  
  for (let i = 0; i < count; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    months.push(`${year}-${month}`);
  }
  
  return months;
}

/**
 * Format month string for display (e.g., "2024-01" -> "Jan 2024")
 */
function formatMonthDisplay(month: string): string {
  const [year, monthNum] = month.split('-');
  const date = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}
